<script lang="ts">
  import { onMount } from 'svelte'
  import { api, type HappRuleSet, type HappTag } from '../lib/api'
  import { notify } from '../lib/toast'
  import Modal from '../components/Modal.svelte'

  let ruleSets = $state<HappRuleSet[]>([])

  let addOpen = $state(false), editOpen = $state(false)
  let editing = $state<HappRuleSet | null>(null)

  let newKey = $state(''), newName = $state(''), newDesc = $state('')
  let editKey = $state(''), editName = $state(''), editDesc = $state(''), editSort = $state(0)

  let newTagInputs = $state<Record<string, string>>({})

  async function load() { ruleSets = await api.listHappRuleSets() }
  onMount(load)

  async function handleAdd() {
    if (!/^[a-z_][a-z0-9_]*$/.test(newKey)) return notify('Неверный ключ', 'err')
    try {
      await api.createHappRuleSet({ key: newKey, name: newName || newKey, description: newDesc || undefined })
      notify('Набор правил создан', 'ok')
      addOpen = false; newKey = ''; newName = ''; newDesc = ''
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  function openEdit(rs: HappRuleSet) {
    editing = rs
    editKey = rs.key; editName = rs.name; editDesc = rs.description || ''; editSort = rs.sortOrder
    editOpen = true
  }

  async function handleEdit() {
    if (!editing) return
    try {
      await api.updateHappRuleSet(editing.id, { key: editKey, name: editName, description: editDesc, sortOrder: editSort })
      notify('Сохранено', 'ok'); editOpen = false; await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function handleDelete() {
    if (!editing) return
    if (!confirm(`Удалить набор ${editing.key}? Все его теги будут удалены.`)) return
    try { await api.deleteHappRuleSet(editing.id); notify('Удалено', 'ok'); editOpen = false; await load() }
    catch (e: any) { notify(e.message, 'err') }
  }

  async function addTag(ruleSetId: number, tagType: 'geosite' | 'geoip') {
    const key = `${ruleSetId}-${tagType}`
    const value = (newTagInputs[key] || '').trim().replace(/^(geosite|geoip):/, '')
    if (!value) return
    try {
      await api.createHappTag({ ruleSetId, tagType, value })
      newTagInputs[key] = ''
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function delTag(id: number) {
    try { await api.deleteHappTag(id); await load() } catch (e: any) { notify(e.message, 'err') }
  }
</script>

<div class="card mb-4">
  <div class="flex items-center justify-between">
    <div>
      <h2 class="font-semibold">Наборы правил Happ</h2>
      <p class="text-xs text-muted mt-1">
        Группы geosite/geoip тегов для Happ routing profile. Используются в Happ-эндпоинтах.
      </p>
    </div>
    <button class="btn btn-primary" onclick={() => (addOpen = true)}>+ Набор</button>
  </div>
</div>

<div class="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
  {#each ruleSets as rs (rs.id)}
    {@const geosite = rs.tags.filter(t => t.tagType === 'geosite')}
    {@const geoip = rs.tags.filter(t => t.tagType === 'geoip')}
    <div class="card" title="key: {rs.key}">
      <div class="flex justify-between items-start gap-2 mb-2">
        <button class="text-left flex-1 cursor-pointer min-w-0" onclick={() => openEdit(rs)}>
          <div class="font-semibold truncate">{rs.name}</div>
          <div class="flex gap-1 mt-2 flex-wrap">
            <span class="badge badge-blue">{geosite.length} geosite</span>
            <span class="badge badge-purple">{geoip.length} geoip</span>
          </div>
          {#if rs.description}<p class="text-xs text-muted mt-2">{rs.description}</p>{/if}
        </button>
        <button class="btn-icon flex-shrink-0" onclick={() => openEdit(rs)} title="Редактировать">✎</button>
      </div>

      <div class="mt-3 pt-3 border-t border-bg-border">
        <div class="label">Geosite</div>
        <div class="flex flex-wrap gap-1">
          {#each geosite as t}
            <span class="chip chip-blue">
              geosite:{t.value}<button class="chip-remove" onclick={() => delTag(t.id)}>×</button>
            </span>
          {/each}
        </div>
        <div class="flex gap-1 mt-2">
          <input class="input input-sm flex-1" placeholder="youtube, telegram..."
                 bind:value={newTagInputs[`${rs.id}-geosite`]}
                 onkeydown={(e) => e.key === 'Enter' && addTag(rs.id, 'geosite')}>
          <button class="btn btn-secondary px-2 py-1 text-xs" onclick={() => addTag(rs.id, 'geosite')}>+</button>
        </div>
      </div>

      <div class="mt-3 pt-3 border-t border-bg-border">
        <div class="label">Geoip</div>
        <div class="flex flex-wrap gap-1">
          {#each geoip as t}
            <span class="chip chip-purple">
              geoip:{t.value}<button class="chip-remove" onclick={() => delTag(t.id)}>×</button>
            </span>
          {/each}
        </div>
        <div class="flex gap-1 mt-2">
          <input class="input input-sm flex-1" placeholder="telegram, facebook..."
                 bind:value={newTagInputs[`${rs.id}-geoip`]}
                 onkeydown={(e) => e.key === 'Enter' && addTag(rs.id, 'geoip')}>
          <button class="btn btn-secondary px-2 py-1 text-xs" onclick={() => addTag(rs.id, 'geoip')}>+</button>
        </div>
      </div>
    </div>
  {/each}
</div>

{#if addOpen}
  <Modal title="Новый набор правил Happ" onclose={() => (addOpen = false)}>
    <div class="space-y-3">
      <div><label class="label">Ключ (латиница)</label><input class="input" bind:value={newKey} placeholder="telegram_rules" autofocus></div>
      <div><label class="label">Название</label><input class="input" bind:value={newName} placeholder="Telegram rules"></div>
      <div><label class="label">Описание (опц.)</label><input class="input" bind:value={newDesc}></div>
    </div>
    {#snippet footer()}
      <button class="btn btn-secondary" onclick={() => (addOpen = false)}>Отмена</button>
      <button class="btn btn-primary" onclick={handleAdd}>Создать</button>
    {/snippet}
  </Modal>
{/if}

{#if editOpen && editing}
  <Modal title="Редактировать набор правил" onclose={() => (editOpen = false)}>
    <div class="space-y-3">
      <div><label class="label">Ключ</label><input class="input" bind:value={editKey}></div>
      <div><label class="label">Название</label><input class="input" bind:value={editName}></div>
      <div><label class="label">Описание</label><input class="input" bind:value={editDesc}></div>
      <div><label class="label">Порядок сортировки</label><input class="input" type="number" bind:value={editSort}></div>
    </div>
    {#snippet footer()}
      <button class="btn btn-danger mr-auto" onclick={handleDelete}>Удалить</button>
      <button class="btn btn-secondary" onclick={() => (editOpen = false)}>Отмена</button>
      <button class="btn btn-primary" onclick={handleEdit}>Сохранить</button>
    {/snippet}
  </Modal>
{/if}
